import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  TextField,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  FormLayout,
} from '@shopify/polaris';
import { patientsApi, clinicsApi, doctorsApi } from '@/services';
import SearchableSelect from '@/components/SearchableSelect';
import type { Patient } from '@/types';

export default function PatientsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const emptyPatient = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referredByDoctorId: '',
    clinicId: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
  };
  const [newPatient, setNewPatient] = useState({ ...emptyPatient });
  const limit = 20;

  // Clinic + doctor pickers for the create modal. These search on the server: the
  // previous version fetched a capped page into a native Select, which cannot be
  // typed into, so with 842 clinics most were simply unreachable.
  const [selectedClinicName, setSelectedClinicName] = useState('');
  const [selectedDoctorName, setSelectedDoctorName] = useState('');

  const searchClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    return res.data.map((c) => ({
      label: c.name,
      value: c.id,
      detail: c.email || undefined,
    }));
  }, []);

  const searchDoctors = useCallback(async (query: string) => {
    const res = await doctorsApi.search(query, 25);
    return res.map((d) => ({
      label: `${d.firstName} ${d.lastName}`,
      value: d.id,
      detail: d.email || undefined,
    }));
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['patients', page, status, searchQuery],
    queryFn: () =>
      patientsApi.list({
        page,
        limit,
        status: status[0],
        search: searchQuery || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (dto: typeof newPatient) =>
      patientsApi.create({
        firstName: dto.firstName,
        lastName: dto.lastName,
        email: dto.email || undefined,
        phone: dto.phone || undefined,
        referredByDoctorId: dto.referredByDoctorId || undefined,
        clinicId: dto.clinicId || undefined,
        streetAddress: dto.streetAddress || undefined,
        city: dto.city || undefined,
        province: dto.province || undefined,
        postalCode: dto.postalCode || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      setShowCreateModal(false);
      setNewPatient({ ...emptyPatient });
    },
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const handleCreatePatient = useCallback(() => {
    if (!newPatient.firstName || !newPatient.lastName) return;
    createMutation.mutate(newPatient);
  }, [newPatient, createMutation]);

  const getStatusBadge = (patientStatus: string) => {
    switch (patientStatus) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'inactive':
        return <Badge>Inactive</Badge>;
      default:
        return <Badge>{patientStatus}</Badge>;
    }
  };

  const rows = (data?.data || []).map(
    (patient: Patient) => [
      <Button key={patient.id} variant="plain" onClick={() => navigate(`/patients/${patient.id}`)}>
        {patient.firstName} {patient.lastName}
      </Button>,
      patient.email || 'N/A',
      patient.phone || 'N/A',
      patient.referredByDoctor ? `Dr. ${patient.referredByDoctor.lastName}` : 'N/A',
      getStatusBadge(patient.status),
      new Date(patient.createdAt).toLocaleDateString(),
    ],
  );

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters =
    status.length > 0
      ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
      : [];

  if (error) {
    return (
      <Page title="Patients">
        <Card>
          <EmptyState
            heading="Error loading patients"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Patients"
      primaryAction={{
        content: 'Add Patient',
        onAction: () => setShowCreateModal(true),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search patients..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No patients found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{ content: 'Add Patient', onAction: () => setShowCreateModal(true) }}
            >
              <p>Add your first patient to get started.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Name', 'Email', 'Phone', 'Doctor', 'Status', 'Created']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} patients
          </Text>
        </Box>
      )}

      {/* Create Patient Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Patient"
        primaryAction={{
          content: 'Add Patient',
          onAction: handleCreatePatient,
          disabled: !newPatient.firstName || !newPatient.lastName,
          loading: createMutation.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setShowCreateModal(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="First Name"
              value={newPatient.firstName}
              onChange={(value) => setNewPatient((prev) => ({ ...prev, firstName: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Last Name"
              value={newPatient.lastName}
              onChange={(value) => setNewPatient((prev) => ({ ...prev, lastName: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Email"
              type="email"
              value={newPatient.email}
              onChange={(value) => setNewPatient((prev) => ({ ...prev, email: value }))}
              autoComplete="off"
            />
            <TextField
              label="Phone"
              type="tel"
              value={newPatient.phone}
              onChange={(value) => setNewPatient((prev) => ({ ...prev, phone: value }))}
              autoComplete="off"
            />
            <TextField
              label="Street Address"
              value={newPatient.streetAddress}
              onChange={(value) => setNewPatient((prev) => ({ ...prev, streetAddress: value }))}
              autoComplete="off"
            />
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="City"
                  value={newPatient.city}
                  onChange={(value) => setNewPatient((prev) => ({ ...prev, city: value }))}
                  autoComplete="off"
                />
                <TextField
                  label="Province"
                  value={newPatient.province}
                  onChange={(value) => setNewPatient((prev) => ({ ...prev, province: value }))}
                  placeholder="ON"
                  autoComplete="off"
                />
                <TextField
                  label="Postal Code"
                  value={newPatient.postalCode}
                  onChange={(value) => setNewPatient((prev) => ({ ...prev, postalCode: value }))}
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
            {/* Server-side search rather than a native Select. These lists are far
                larger than a dropdown can show — 842 clinics against a 200 cap meant
                roughly three-quarters were unreachable, with nothing on screen to say
                so. */}
            <SearchableSelect
              label="Referring Doctor (optional)"
              value={newPatient.referredByDoctorId}
              selectedLabel={selectedDoctorName}
              onChange={(value, option) => {
                setNewPatient((prev) => ({ ...prev, referredByDoctorId: value }));
                setSelectedDoctorName(option?.label ?? '');
              }}
              onSearch={searchDoctors}
              emptyOption={{ label: 'No doctor', value: '' }}
              placeholder="Type a doctor's name…"
            />
            <SearchableSelect
              label="Clinic (optional)"
              value={newPatient.clinicId}
              selectedLabel={selectedClinicName}
              onChange={(value, option) => {
                setNewPatient((prev) => ({ ...prev, clinicId: value }));
                setSelectedClinicName(option?.label ?? '');
              }}
              onSearch={searchClinics}
              emptyOption={{ label: 'No clinic', value: '' }}
              placeholder="Type a clinic name…"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
